// MUNYA OS · R0 responsive · barra de pestañas inferior (móvil/tablet <lg)
// Patrón app nativa (decisión Jorge 2-jul): 4 espacios principales + hoja "Más" con el resto,
// misma matriz de visibilidad por rol que el header (nav-shared). Safe-area inferior para el
// home-indicator de iOS (wrapper Capacitor). En ≥lg no se renderiza (nav superior de siempre).

import { useEffect, useState, type ReactElement } from 'react';
import { DotsThreeOutline, MagnifyingGlass, SignOut, X } from '@phosphor-icons/react';
import { Link, usePathname } from '../lib/router.js';
import { unregisterNativeToken } from '../lib/native.js';
import { activeHrefOf, useVisibleNav, type NavItem } from './nav-shared.js';
import { useDismissable } from '../lib/use-dismissable.js';

const MAX_PRIMARY = 4;

export function MobileTabBar(): ReactElement | null {
  const nav = useVisibleNav();
  const pathname = usePathname();
  const [moreOpen, setMoreOpen] = useState(false);

  // Cierra la hoja al navegar y con Escape
  useEffect(() => {
    setMoreOpen(false);
  }, [pathname]);
  useEffect(() => {
    if (!moreOpen) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setMoreOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [moreOpen]);

  // · #456 · el fondo solo cierra con el gesto completo (mousedown + mouseup sobre el fondo)
  const { backdropProps } = useDismissable({
    onDismiss: () => {
      setMoreOpen(false);
    },
  });

  if (nav.length === 0) return null;
  const needsMore = nav.length > MAX_PRIMARY + 1;
  const primary = needsMore ? nav.slice(0, MAX_PRIMARY) : nav;
  const overflow = needsMore ? nav.slice(MAX_PRIMARY) : [];
  const activeHref = activeHrefOf(nav, pathname);
  const activeInOverflow = overflow.some((it) => it.href === activeHref);

  return (
    <>
      <nav
        aria-label="Navegación principal"
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-[var(--color-munya-surface)] border-t border-[var(--color-munya-sand)] mn-safe-bottom mn-safe-x"
        data-focus-hide
      >
        <div className="flex items-stretch justify-around h-[3.5rem]">
          {primary.map((item) => (
            <TabItem key={item.href} item={item} active={item.href === activeHref} />
          ))}
          {overflow.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setMoreOpen(true);
              }}
              aria-label="Más espacios"
              aria-expanded={moreOpen}
              className={`flex flex-col items-center justify-center gap-0.5 flex-1 min-w-0 cursor-pointer transition-colors ${
                activeInOverflow
                  ? 'text-[var(--color-munya-olive)]'
                  : 'text-[var(--color-munya-coffee)]/60 hover:text-[var(--color-munya-coffee)]'
              }`}
            >
              <DotsThreeOutline size={22} weight={activeInOverflow ? 'fill' : 'regular'} />
              <span className="text-[10px] leading-none">Más</span>
            </button>
          )}
        </div>
      </nav>

      {moreOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex items-end bg-black/50" {...backdropProps}>
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Más espacios"
            className="w-full bg-[var(--color-munya-surface)] rounded-t-2xl shadow-2xl animate-in-sheet mn-safe-bottom"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <header className="flex items-center justify-between px-5 pt-4 pb-2">
              <h2 className="text-sm font-semibold">Más espacios</h2>
              <button
                type="button"
                onClick={() => {
                  setMoreOpen(false);
                }}
                aria-label="Cerrar"
                className="p-2 -m-2 opacity-60 hover:opacity-100 cursor-pointer transition-opacity"
              >
                <X size={18} />
              </button>
            </header>
            <div className="grid grid-cols-4 gap-1 px-3 pb-2">
              {overflow.map((item) => {
                const Icon = item.icon;
                const active = item.href === activeHref;
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={`flex flex-col items-center gap-1.5 rounded-xl px-1 py-3 min-h-[64px] transition-colors ${
                      active
                        ? 'bg-[var(--color-munya-olive)]/15 text-[var(--color-munya-olive)] font-medium'
                        : 'text-[var(--color-munya-coffee)]/80 hover:bg-[var(--color-munya-sand)]/30'
                    }`}
                  >
                    <Icon size={22} weight={active ? 'bold' : 'regular'} />
                    <span className="text-[11px] leading-tight text-center">{item.label}</span>
                  </Link>
                );
              })}
            </div>
            <div className="border-t border-[var(--color-munya-sand)] px-3 py-2 flex items-center justify-between">
              <button
                type="button"
                onClick={() => {
                  setMoreOpen(false);
                  // Reutiliza el atajo global ⌘K → abre la CommandPalette
                  window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }));
                }}
                className="inline-flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-[var(--color-munya-coffee)]/80 hover:bg-[var(--color-munya-sand)]/30 cursor-pointer transition-colors"
              >
                <MagnifyingGlass size={18} /> Buscar
              </button>
              <a
                href="/auth/logout"
                onClick={() => {
                  unregisterNativeToken();
                }}
                className="inline-flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-[var(--color-munya-coffee)]/80 hover:bg-[var(--color-munya-sand)]/30 transition-colors"
              >
                <SignOut size={18} /> Salir
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function TabItem({ item, active }: { item: NavItem; active: boolean }): ReactElement {
  const Icon = item.icon;
  return (
    <Link
      href={item.href}
      aria-current={active ? 'page' : undefined}
      className={`flex flex-col items-center justify-center gap-0.5 flex-1 min-w-0 transition-colors ${
        active
          ? 'text-[var(--color-munya-olive)]'
          : 'text-[var(--color-munya-coffee)]/60 hover:text-[var(--color-munya-coffee)]'
      }`}
    >
      <Icon size={22} weight={active ? 'bold' : 'regular'} />
      <span className="text-[10px] leading-none truncate max-w-full px-0.5">{item.label}</span>
    </Link>
  );
}
